import type { Editor } from 'hugerte';

import { stampImageDimensions } from './stampImageDimensions';

/** The iframe has its own realm, so `instanceof HTMLImageElement` would always be false for its nodes. */
const isImage = (target: EventTarget | null): target is HTMLImageElement =>
	target !== null && 'tagName' in target && target.tagName === 'IMG';

export const setupImageDimensions = (editor: Editor): void => {
	const doc = editor.getDoc();

	// `load` doesn't bubble, hence the capture phase
	doc.addEventListener(
		'load',
		(event) => {
			if (!isImage(event.target)) return;
			const stamped = stampImageDimensions(editor, event.target);
			// Once the user has changed something (an image inserted, or swapped for its uploaded file, loads after the
			// change event that announced it), consumers hold a value without the dimensions: notify them again.
			// Before that, the content is still the saved one and stamping must not make it look edited. The undo stack
			// tells them apart: unlike the dirty flag it survives `save()`, and the editor's own `setContent` calls on
			// start-up leave it untouched.
			if (stamped && editor.undoManager.hasUndo()) editor.undoManager.dispatchChange();
		},
		true,
	);

	// images that finished loading before the listener was attached (e.g. cached ones)
	Array.from(doc.images).forEach((image) => {
		if (image.complete) stampImageDimensions(editor, image);
	});
};
